/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client/react';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Button, Divider, Flex, Loader, Text } from '@opendoor/bricks/core';

import {
  IUpdateUserNotificationPreferences,
  IUpdateUserNotificationPreferencesVariables,
  UPDATE_USER_NOTIFICATION_PREFERENCES,
} from '../graphql/mutations/UpdateNotificationPreferences';
import {
  IUserNotificationPreferences,
  IUserNotificationPreferencesVariables,
  USER_NOTIFICATION_PREFERENCES,
} from '../graphql/queries/GetNotificationPreferences';
import trackEvent from '../helpers/trackEvent';
import { ERROR_PAGE_ROUTE } from '../routes/routes';
import NotificationPreferenceCard from './NotificationPreferenceCard';

export interface INotificationCategory {
  categoryKey: string;
  label?: string;
  description?: string;
  enabled: boolean;
}

export interface INotificationPreferencesProps {
  userId?: string;
  withoutHeader?: boolean;
}

export const NOTIFICATION_CATEGORIES = [
  {
    categoryKey: 'InitialLeadNotifications',
    label: 'Offer',
    description: 'Get notified when an offer has been viewed, sent, expired, denied, or refreshed.',
  },
  {
    categoryKey: 'InspectionNotifications',
    label: 'Assessment',
    description: 'Get notified when your lead completes their exterior or interior assessment.',
  },
  {
    categoryKey: 'FinalOfferNotifications',
    label: 'Final Offer',
    description: 'Get notified when your lead receives their final offer.',
  },
  {
    categoryKey: 'PostOfferNotifications',
    label: 'Contract & Closing',
    description:
      'Get notified when a contract is signed, when contingencies are released, and other closing milestones.',
  },
];

const NotificationPreferences: React.FC<INotificationPreferencesProps> = (
  props: INotificationPreferencesProps,
) => {
  const { userId } = props;
  const [localNotificationPreferences, setLocalNotificationPreferences] = useState<
    Array<INotificationCategory>
  >([]);
  const [originalNotificationPreferences, setOriginalNotificationPreferences] = useState<
    Array<INotificationCategory>
  >([]);
  const [updateButtonEnabled, setUpdateButtonEnabled] = useState<boolean>(false);

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery<IUserNotificationPreferences, IUserNotificationPreferencesVariables>(
    USER_NOTIFICATION_PREFERENCES,
    {
      fetchPolicy: 'no-cache',
      variables: { userId },
    },
  );

  const [
    updateNotificationPreferencesMutation,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useMutation<IUpdateUserNotificationPreferences, IUpdateUserNotificationPreferencesVariables>(
    UPDATE_USER_NOTIFICATION_PREFERENCES,
  );
  const handleSubmit = async () => {
    trackEvent('click', 'update-notification-preferences');
    await updateNotificationPreferencesMutation({
      variables: {
        params: {
          userId,
          notificationPreferences: localNotificationPreferences.map((preference) => {
            return {
              categoryKey: preference.categoryKey,
              enabled: preference.enabled,
            };
          }),
        },
      },
    });
  };

  useEffect(() => {
    if (mutationData || mutationError) {
      const toastMessage = mutationData
        ? 'Your email preferences have been updated.'
        : 'Unable to update email preferences. Please try again.';
      showToast(
        'info',
        toastMessage,
        'partnership-dashboard-fe-update-email-preferences-info-toast',
        { toastOptions: { autoClose: false } },
      );
    }
  }, [mutationData, mutationError]);

  useEffect(() => {
    if (queryData || mutationData) {
      const preferences: Array<INotificationCategory> = mutationData
        ? mutationData.updateUserNotificationPreferences.notificationPreferences
        : queryData!.userNotificationPreferences.notificationPreferences;
      setOriginalNotificationPreferences(
        NOTIFICATION_CATEGORIES.filter((category) => {
          return preferences.find((preference) => preference.categoryKey === category.categoryKey);
        }).map((category) => {
          const preference = preferences.find(
            (preference) => preference.categoryKey === category.categoryKey,
          );
          return {
            categoryKey: category.categoryKey,
            label: category.label,
            description: category.description,
            enabled: preference!.enabled,
          };
        }),
      );
    }
  }, [queryData, mutationData]);

  useEffect(() => {
    setLocalNotificationPreferences(originalNotificationPreferences);
  }, [originalNotificationPreferences]);

  const updateNotificationCategories = (categoryKey: string, enabled: boolean) => {
    setLocalNotificationPreferences((notificationPreferences) => {
      const preferences = notificationPreferences.map((preference) => {
        if (preference.categoryKey === categoryKey) {
          return {
            ...preference,
            enabled,
          };
        }

        return { ...preference };
      });
      return preferences;
    });
  };

  useEffect(() => {
    setUpdateButtonEnabled(
      JSON.stringify(originalNotificationPreferences) !==
        JSON.stringify(localNotificationPreferences),
    );
    // eslint-disable-next-line
  }, [localNotificationPreferences]);

  if (queryLoading) {
    return <Loader />;
  }

  if (queryError) {
    return <Redirect to={ERROR_PAGE_ROUTE} />;
  }

  return (
    <>
      {!props.withoutHeader && (
        <Box margin="auto">
          <Text pb={6} pt={6} fontWeight="bold" fontSize="s2" textAlign="center">
            Email Preferences
          </Text>
          <Flex display={['none', null, 'block']}>
            <Divider />
          </Flex>
        </Box>
      )}
      <Box px={7} pt={6} pb={11} margin="auto" maxWidth="580px">
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <Text fontSize="s1" lineHeight="s0" my={7}>
          Email preferences can be set for each phase of the Opendoor selling process. You can
          toggle different phases based on what you'd like to get notified about.
        </Text>
        {localNotificationPreferences.map((preference, index) => (
          <NotificationPreferenceCard
            key={index}
            notificationPreference={preference}
            updateNotificationCategories={updateNotificationCategories}
          />
        ))}

        <Button
          onClick={handleSubmit}
          disabled={!updateButtonEnabled || mutationLoading}
          loading={mutationLoading}
          loadingText={'Saving email preferences'}
          analyticsName="partnership-dashboard-fe-update-email-preferences"
          aria-label=""
        >
          Update email preferences
        </Button>
      </Box>
    </>
  );
};

export default NotificationPreferences;
